// features/tour/GuidedTour.tsx
//
// Visual layer for the guided tour:
//   • a first-run invite card (offered once per account), and
//   • the running overlay — a dimming backdrop with an optional spotlight ring
//     around a target element, plus a step card with Back / Next / Skip.
//
// All styling is MUI + theme tokens, so it follows light/dark automatically.

import { useEffect, useState, Fragment, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, IconButton, Fade, Grow, alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTour } from './TourProvider';

const OVERLAY_Z = 13000;

interface Rect { top: number; left: number; width: number; height: number; }

/** Poll for `selector` for a short window (navigation is async) and track its rect. */
function useSpotlight(selector: string | undefined, stepKey: number): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!selector) { setRect(null); return; }
    let raf = 0;
    let tries = 0;
    let stopped = false;

    const measure = () => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        return true;
      }
      return false;
    };

    const poll = () => {
      if (stopped) return;
      if (measure()) return;
      tries += 1;
      if (tries < 60) raf = requestAnimationFrame(poll); // ~1s at 60fps
      else setRect(null);
    };
    poll();

    const onMove = () => { if (!stopped) measure(); };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [selector, stepKey]);

  return rect;
}

function ProgressDots({ count, current }: { count: number; current: number }): ReactElement {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
      {Array.from({ length: count }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: i === current ? 18 : 7,
            height: 7,
            borderRadius: 4,
            transition: 'all 0.25s ease',
            backgroundColor: (t) => i === current
              ? t.palette.primary.main
              : alpha(t.palette.text.primary, 0.2),
          }}
        />
      ))}
    </Box>
  );
}

/** The running tour overlay. */
function TourOverlay(): ReactElement | null {
  const { active, index, next, prev, stop } = useTour();
  const navigate = useNavigate();
  const step = active?.steps[index];
  const rect = useSpotlight(step?.selector, index);

  // Keyboard controls.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stop();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, next, prev, stop]);

  if (!active || !step) return null;

  const isLast = index === active.steps.length - 1;
  const isFirst = index === 0;
  const pad = 8;

  return (
    <Fade in timeout={250}>
      <Box sx={{ position: 'fixed', inset: 0, zIndex: OVERLAY_Z, pointerEvents: 'none' }}>
        {/* Backdrop — a spotlight cutout when we have a target, else a flat dim. */}
        {rect ? (
          <Box
            sx={{
              position: 'fixed',
              top: rect.top - pad,
              left: rect.left - pad,
              width: rect.width + pad * 2,
              height: rect.height + pad * 2,
              borderRadius: 2,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.62)',
              border: (t) => `2px solid ${t.palette.primary.main}`,
              transition: 'all 0.3s ease',
              animation: 'rmTourPulse 2s ease-in-out infinite',
              '@keyframes rmTourPulse': {
                '0%, 100%': { boxShadow: '0 0 0 9999px rgba(0,0,0,0.62), 0 0 0 0 rgba(99,102,241,0.45)' },
                '50%': { boxShadow: '0 0 0 9999px rgba(0,0,0,0.62), 0 0 0 8px rgba(99,102,241,0)' },
              },
            }}
          />
        ) : (
          <Box sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.62)' }} />
        )}

        {/* Step card — pinned bottom-centre, above the backdrop. */}
        <Grow in timeout={300} key={index}>
          <Paper
            elevation={12}
            sx={{
              pointerEvents: 'auto',
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: { xs: 16, sm: 32 },
              width: 'calc(100% - 32px)',
              maxWidth: 460,
              borderRadius: 3,
              overflow: 'hidden',
              border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.25)}`,
            }}
          >
            {/* Accent header strip */}
            <Box
              sx={{
                px: 2.5, py: 1.25,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: (t) => `linear-gradient(90deg, ${alpha(t.palette.primary.main, 0.18)}, ${alpha(t.palette.info.main, 0.18)})`,
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.4, color: 'text.secondary' }}>
                {active.label.toUpperCase()} · {index + 1}/{active.steps.length}
              </Typography>
              <IconButton size="small" onClick={stop} aria-label="Close tour" sx={{ color: 'text.secondary' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.08rem', mb: 1 }}>
                {step.title}
              </Typography>
              {step.body.split('\n').filter(Boolean).map((para, i) => (
                <Typography key={i} sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.55, mb: 0.75 }}>
                  {para}
                </Typography>
              ))}
              {step.tip && (
                <Box
                  sx={{
                    mt: 1, px: 1.25, py: 0.75, borderRadius: 2,
                    display: 'flex', gap: 0.75, alignItems: 'flex-start',
                    backgroundColor: (t) => alpha(t.palette.primary.main, 0.1),
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 16, color: 'primary.main', mt: '2px' }} />
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.primary' }}>{step.tip}</Typography>
                </Box>
              )}
            </Box>

            {/* Controls */}
            <Box sx={{ px: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Button size="small" onClick={stop} sx={{ color: 'text.secondary', textTransform: 'none' }}>
                Skip
              </Button>
              <ProgressDots count={active.steps.length} current={index} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!isFirst && (
                  <Button
                    size="small" onClick={prev} startIcon={<ArrowBackIcon sx={{ fontSize: 12 }} />}
                    sx={{ textTransform: 'none' }}
                  >
                    Back
                  </Button>
                )}
                {step.cta ? (
                  <Button
                    size="small" variant="contained"
                    onClick={() => { const to = step.cta!.route; stop(); navigate(to); }}
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 12 }} />}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    {step.cta.label}
                  </Button>
                ) : (
                  <Button
                    size="small" variant="contained" onClick={next}
                    endIcon={isLast ? undefined : <ArrowForwardIcon sx={{ fontSize: 12 }} />}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    {isLast ? 'Finish' : 'Next'}
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Grow>
      </Box>
    </Fade>
  );
}

/** Mounted once at the app root. Renders the running page tour, if any. */
export function GuidedTour(): ReactElement {
  return (
    <Fragment>
      <TourOverlay />
    </Fragment>
  );
}

export default GuidedTour;
